// src/pages/Signup.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { signupAccount, clearSignupStatus } from "../slice/signupSlice";
import { auth } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import axiosInstance from "../apis/axiosConfig";
import { useDarkMode } from "../hooks/useDarkMode";

const Signup = () => {
  const { isDarkMode } = useDarkMode();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { loading, success, error } = useSelector((state) => state.signup);

  const [formData, setFormData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [passwordError, setPasswordError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (success) {
      setTimeout(() => {
        dispatch(clearSignupStatus());
        navigate("/signin");
      }, 2000);
    }
  }, [success, navigate, dispatch]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (e.target.name === "confirmPassword" || e.target.name === "password") {
      setPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setPasswordError("Passwords do not match!");
      return;
    }

    setSubmitting(true);
    setPasswordError("");

    try {
      // If Firebase `auth` is initialized, create user there and store ID token.
      if (auth) {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        const user = userCredential.user;
        const idToken = await user.getIdToken();

        localStorage.setItem('token', idToken);
        localStorage.setItem('uid', user.uid); // Store Firebase UID for account identification

        // Save basic account locally for UI (profile stored in local storage)
        const accountData = {
          uid: user.uid,
          firstname: formData.firstname,
          lastname: formData.lastname,
          email: formData.email,
          displayName: `${formData.firstname} ${formData.lastname}`,
        };
        
        localStorage.setItem('account', JSON.stringify(accountData));
        localStorage.setItem('user', JSON.stringify(accountData));
        localStorage.setItem('userName', accountData.displayName);
        localStorage.setItem('userEmail', formData.email);

        // Redirect to dashboard
        navigate('/dashboard');
      } else {
        // Fallback to existing backend signup API (may return 404 if backend doesn't expose this)
        await dispatch(signupAccount(formData));
        navigate('/otpverify', { state: { email: formData.email } });
      }
    } catch (err) {
      console.error('Signup failed', err);
      setPasswordError(err?.message || 'Signup failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-green-50 to-green-100">
    {/* ===== BACKGROUND CIRCLES ===== */}
      <div className="absolute -top-24 -left-24 w-72 h-72 bg-[#008300]/10 rounded-full"></div>
      <div className="absolute top-40 right-0 w-64 h-64 bg-[#0B1B3F]/10 rounded-full"></div>
      <div className="bg-white p-8 rounded-2xl shadow-lg w-full max-w-md">
        <h2 className="text-3xl font-semibold text-center text-gray-800 mb-6">
          Create <span className="text-[#008300]">Account</span>
        </h2>

        {/* Error & Success Messages */}
        {error && <p className="text-red-500 text-sm mb-3 text-center">{error}</p>}
        {passwordError && (
          <p className="text-red-500 text-sm mb-3 text-center">{passwordError}</p>
        )}
        {success && (
          <p className="text-green-600 text-sm mb-3 text-center">
            Signup successful! Redirecting...
          </p>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <div className="w-1/2">
              <label className="block text-gray-700 font-medium mb-1">
                First Name
              </label>
              <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                placeholder="John"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#008300] focus:outline-none"
              />
            </div>
            <div className="w-1/2">
              <label className="block text-gray-700 font-medium mb-1">
                Last Name
              </label>
              <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                placeholder="Doe"
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="you@example.com"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#008300] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="********"
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#008300] focus:outline-none"
            />
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="********"
              required
              className={`w-full px-4 py-2 border ${passwordError ? "border-red-400" : "border-gray-300"
                } rounded-md focus:ring-2 focus:ring-[#008300] focus:outline-none`}
            />
          </div>

          <button
            type="submit"
            disabled={loading || submitting}
            className="w-full bg-[#008300] text-white py-2 rounded-md hover:bg-[#006600] transition-all duration-300 font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading || submitting ? "Creating Account..." : "Sign Up"}
          </button>
        </form>

        <p className="text-center text-gray-600 text-sm mt-4">
          Already have an account?{" "}
          <Link to="/signin" className="text-[#008300] font-medium hover:underline">
            Sign In
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Signup;
